/**
 * Centralized filtering of table attribute lists by tier2 entitlements.
 *
 * Callers pass the list of attributes they want to request and the entitlements
 * (allowed list from tier2). The function returns requestedAttributes filtered
 * by entitlements; when restricted, Id (and DisplayName for non–Journal Entry)
 * are always included so the table can render. Callers must not call the API
 * until tier2 has resolved (allowedTableAttributeIds !== null).
 * Journal Entry includes DisplayName in always-included attrs (computed on the server).
 *
 * Table-fetch entry points (and why they are not a single generic fetch):
 * - DynamicTableView.defaultLoadData: generic getEntitiesByType; uses this util.
 * - JournalEntriesView.loadJournalEntries: Journal Entry only; uses this util.
 * - PersonManagement.loadDataForHook: getEntitiesByType or searchPersons; own attribute list.
 * - GroupsManagement.loadData / useGroupsData: searchGroups; own attribute list.
 * - Locality tables: service.search(); no attributes param.
 * - SchemaBindingManagement, SetForm, EntityBrowserDialog, etc.: one-off fetches.
 *
 * Counter-indicators to a single generic table fetch:
 * - Different APIs: getEntitiesByType vs searchGroups vs searchPersons.
 * - Custom loadData override: parent owns the fetch and attribute list.
 * - Locality tables: no attributes parameter.
 */

export interface FilterAttributesByEntitlementsOptions {
    /** When true, journal-entry attribute filtering (Id + DisplayName always included). Default false. */
    isJournalEntry?: boolean;
}

/**
 * Filters a list of requested attributes by tier2 entitlements.
 * Use this before passing attributes to getEntitiesByType (or equivalent).
 *
 * @param requestedAttributes - Attributes the caller wants to request (e.g. default list for the entity type).
 * @param allowedTableAttributeIds - From usePerEntityTableEntitlement: null = loading (do not call API), undefined = no restriction (admin), string[] = only these attributes allowed.
 * @param options - Optional: { isJournalEntry } to control always-included attributes (Id only vs Id + DisplayName).
 * @returns When allowed is array: requestedAttributes filtered to those in allowed, plus Id (and DisplayName unless isJournalEntry). When undefined or null: requestedAttributes unchanged. Callers must not call the API when allowed is null.
 */
export function filterRequestedAttributesByEntitlements(
    requestedAttributes: string[],
    allowedTableAttributeIds: string[] | undefined | null,
    _options?: FilterAttributesByEntitlementsOptions
): string[] {
    if (!Array.isArray(allowedTableAttributeIds)) {
        return requestedAttributes;
    }

    const allowedSet = new Set(allowedTableAttributeIds.map((a) => (a ?? '').trim().toLowerCase()));
    const filtered = requestedAttributes.filter((a) => {
        const key = (a ?? '').trim().toLowerCase();
        return key && allowedSet.has(key);
    });

    const alwaysInclude = ['Id', 'DisplayName'];
    return [...new Set([...alwaysInclude, ...filtered])];
}

/** Default attributes to request per entity type (use as requestedAttributes then filter by entitlements). */
export const DEFAULT_TABLE_ATTRIBUTES_BY_ENTITY_TYPE: Record<string, string[]> = {
    set: ['Id', 'DisplayName', 'EntityType', 'FilterScope', 'IsSystem', 'Type', 'Description'],
    studentbusstop: ['Id', 'DisplayName', 'EntityType', 'Student', 'Neighborhood', 'Zip', 'Address'],
    schemaentity: ['Id', 'DisplayName', 'EntityType', 'Description', 'IsEntitySet', 'Disjointed', 'Invokable'],
    // Omit Entities, IsReferenceTo, Bindings from list (large arrays, fetch in detail only)
    schemaattribute: ['Id', 'DisplayName', 'EntityType', 'Type', 'Array', 'Indexed', 'IsReference', 'Computed', 'Required', 'Description'],
    workflow: ['Id', 'DisplayName', 'EntityType', 'Type'],
    workflowactivity: ['Id', 'DisplayName', 'EntityType', 'Action', 'Authorization', 'Request'],
    policyrule: ['Id', 'DisplayName', 'EntityType', 'IsSystem', 'Disabled', 'Description'],
    apiuser: ['Id', 'DisplayName', 'EntityType', 'UserPrincipalName'],
    emailtemplate: ['Id', 'DisplayName', 'EntityType', 'Subject', 'Description', 'ContentType'],
    content: ['Id', 'DisplayName', 'EntityType', 'Subject', 'Tags', 'ContentType'],
    person: ['Id', 'DisplayName', 'EntityType', 'Email', 'UserPrincipalName', 'Photo'],
    employee: ['Id', 'DisplayName', 'Email', 'UserPrincipalName', 'Department', 'JobTitle', 'Photo'],
    student: ['Id', 'DisplayName', 'Email', 'UserPrincipalName', 'GradeLevel', 'School', 'Photo'],
    applicant: ['Id', 'DisplayName', 'Email', 'JobCode', 'UserPrincipalName', 'Photo'],
    contractor: ['Id', 'DisplayName', 'Email', 'JobCode', 'UserPrincipalName', 'Vendor', 'Photo'],
    vacancy: ['Id', 'DisplayName', 'UserPrincipalName', 'JobTitle', 'Position'],
    sponsored: ['Id', 'DisplayName', 'Email', 'UserPrincipalName', 'Photo'],
    shared: ['Id', 'DisplayName', 'Email', 'UserPrincipalName'],
    public: ['Id', 'DisplayName', 'Email', 'UserPrincipalName'],
    requestapprovaltype: ['Id', 'DisplayName', 'EntityType', 'Type', 'Description', 'DurationDays', 'ActorThreshold', 'IsSystem'],
    // Omit Decisions, Operations, ExplicitActors from list (large/complex, fetch in detail only)
    requestapproval: ['Id', 'DisplayName', 'EntityType', 'Status', 'Type', 'ModificationType', 'Target', 'TargetType', 'Requestor', 'RequestorType', 'RequestCreated', 'RequestExpiration'],
    settransition: ['Id', 'DisplayName', 'EntityType', 'Direction', 'TargetSet', 'ActionWorkflow', 'Disabled'],
    // Intentionally omit Data from list requests: it is binary payload. Fetch it only for detail/download.
    file: ['Id', 'DisplayName', 'EntityType', 'ContentType', 'FileName', 'Tags'],
    // Photo: omit Data from list (binary); load it only when the user opens a specific photo.
    photo: ['Id', 'DisplayName', 'EntityType', 'ContentType', 'Owner', 'OwnerType', 'SchoolYear', 'Size'],
    spooledemail: ['Id', 'DisplayName', 'EntityType', 'Status', 'From', 'To', 'SendAttempts', 'Recorded', 'LastSendAttempt'],
    // Intentionally omit Operation from list requests: it can be large. Fetch it only for detail/revert.
    journalentry: ['Id', 'DisplayName', 'TargetType', 'Attribute', 'TargetId', 'Recorded', 'Source', 'Status'],
    modificationerror: ['Id', 'DisplayName', 'EntityType', 'ErrorMessage', 'ModificationType', 'Source', 'Recorded', 'ActorDisplayName', 'TargetDisplayName', 'TargetType', 'Acknowledged'],
    appregistration: ['Id', 'DisplayName', 'EntityType', 'ClientId', 'TenantId', 'Disabled', 'IsSystem'],
    credential: ['Id', 'DisplayName', 'EntityType', 'Domain', 'Username', 'Disabled', 'IsSystem', 'Description'],
};

/**
 * Returns the default requested attributes for an entity type (for use with filterRequestedAttributesByEntitlements).
 */
export function getDefaultRequestedAttributesForEntityType(entityType: string): string[] {
    const normalized = (entityType || '').toLowerCase().replace(/\s+/g, '');
    return DEFAULT_TABLE_ATTRIBUTES_BY_ENTITY_TYPE[normalized] ?? ['Id', 'DisplayName', 'EntityType'];
}
